<template>
  <teleport to="body" :disabled="!isMounted">
    <transition-group
      tag="div"
      name="toast"
      class="fixed top-4 right-4 z-50 space-y-3 max-w-sm"
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="translate-x-full opacity-0"
      enter-to-class="translate-x-0 opacity-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="translate-x-0 opacity-100"
      leave-to-class="translate-x-full opacity-0"
    >
      <div
        v-for="toast in toasts"
        :key="toast.id"
        :class="toastClasses(toast.type)"
        class="relative flex items-start p-4 rounded-mh-lg shadow-lift backdrop-blur-sm max-w-sm"
        role="alert"
        :aria-labelledby="`toast-title-${toast.id}`"
        :aria-describedby="toast.message ? `toast-message-${toast.id}` : undefined"
      >
        <!-- Icono -->
        <div class="flex-shrink-0 mr-3">
          <!-- Success Icon -->
          <svg v-if="toast.type === 'success'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <!-- Error Icon -->
          <svg v-else-if="toast.type === 'error'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <!-- Warning Icon -->
          <svg v-else-if="toast.type === 'warning'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <!-- Info Icon -->
          <svg v-else-if="toast.type === 'info'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>

        <!-- Contenido -->
        <div class="flex-1 min-w-0">
          <h4 :id="`toast-title-${toast.id}`" class="font-medium text-sm leading-5">
            {{ toast.title }}
          </h4>
          <p
            v-if="toast.message"
            :id="`toast-message-${toast.id}`"
            class="mt-1 text-sm opacity-90 leading-4"
          >
            {{ toast.message }}
          </p>
        </div>

        <!-- Botón cerrar (toast-close amplía el área táctil a 44px) -->
        <button
          @click="removeToast(toast.id)"
          class="toast-close relative flex-shrink-0 ml-4 p-1 rounded-md hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50 transition-colors"
          :aria-label="`Cerrar notificación: ${toast.title}`"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <!-- Barra de progreso para auto-dismiss -->
        <div
          v-if="!toast.persistent && toast.duration"
          class="toast-progress absolute bottom-0 left-0 h-1 bg-white/30 rounded-b-lg"
          :style="{
            animation: `toast-progress ${toast.duration}ms linear forwards`,
            width: '100%'
          }"
        />
      </div>
    </transition-group>
  </teleport>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useToast, type ToastType } from '@/composables/useToast'
// Iconos SVG inline para evitar dependencias adicionales

const { toasts, removeToast } = useToast()

// Control de montaje para teleport seguro
const isMounted = ref(false)

onMounted(() => {
  isMounted.value = true
})

// ========== COMPUTED ==========

/**
 * Clases CSS para cada tipo de toast
 */
const toastClasses = (type: ToastType) => {
  const baseClasses = 'text-white border-l-4'

  // Paleta MINDHOME: navy de autoridad + mint (éxito) / sage / error.
  // El warning usa el sage de texto como fondo: sobre el sage claro el
  // texto blanco quedaba a 2.2:1 (AA exige 4.5:1).
  const typeClasses = {
    success: 'bg-navy border-l-mint text-white',
    error: 'bg-error border-l-white/60 text-white',
    warning: 'toast-bg-warning border-l-mint text-white',
    info: 'bg-navy border-l-blue-light text-white'
  }

  return `${baseClasses} ${typeClasses[type]}`
}

// Iconos implementados inline en el template
</script>

<style scoped>
.toast-bg-warning {
  /* --sage-bg es estable entre temas: --sage-text en oscuro se aclara
     (es un token de TEXTO) y devolvería el blanco fijo a 2.2:1 */
  background: var(--sage-bg);
}

/* Con movimiento reducido la barra se congela en su primer frame y
   desaparecería al instante: mejor no mostrar una cuenta atrás falsa */
@media (prefers-reduced-motion: reduce) {
  .toast-progress {
    display: none;
  }
}

/* Área táctil de 44px sin agrandar el icono */
.toast-close::after {
  content: '';
  position: absolute;
  inset: -10px;
}

/* Animación para la barra de progreso */
@keyframes toast-progress {
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
}

/* Transiciones para el grupo de toasts */
.toast-move,
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.toast-leave-active {
  position: absolute;
  right: 0;
}
</style>